import React, { useEffect, useState } from 'react';
import { useKeycloakAuth } from '../contexts/KeycloakAuthProvider';
import { Navigate } from 'react-router-dom';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isSignedIn, isInitialized, isAdmin, login: _login } = useKeycloakAuth();
  const [isAdminUser, setIsAdminUser] = useState<boolean | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (isSignedIn && isInitialized) {
        try {
          const adminStatus = await isAdmin();
          setIsAdminUser(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdminUser(false);
        }
      }
      setIsCheckingAdmin(false);
    };

    checkAdminStatus();
  }, [isSignedIn, isInitialized, isAdmin]);

  // Show loading spinner while authentication is initializing or checking admin status
  if (!isInitialized || isCheckingAdmin) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-background'>
        <div className='text-center'>
          <div className='inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent'></div>
          <p className='mt-4 text-gray-600 dark:text-dark-text-secondary'>Đang kiểm tra quyền truy cập...</p>
        </div>
      </div>
    );
  }

  // If user is not signed in, redirect to auth page
  if (!isSignedIn) {
    return <Navigate to='/auth' replace />;
  }

  // If user is signed in but not admin, show access denied
  if (!isAdminUser) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-background'>
        <div className='text-center'>
          <div className='mb-4 text-6xl'>🚫</div>
          <h1 className='mb-4 text-2xl font-bold text-gray-900 dark:text-dark-text'>Không có quyền truy cập</h1>
          <p className='mb-6 text-gray-600 dark:text-dark-text-secondary'>Bạn cần có quyền quản trị viên để truy cập trang này.</p>
          <button
            onClick={() => window.history.back()}
            className='rounded-lg bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has admin privileges, render children
  return <>{children}</>;
};

export default AdminRoute;
